import { memo } from "react";

import { Locale, ShortWarning, TableData } from "../types";
import { COLOR, CTA_ENDPOINT, CTA_HR_ENDPOINT, CTA_RO_ENDPOINT, CTA_SK_ENDPOINT } from "../config";

import localization from "../localization";

import {
  Box,
  Button,
  Divider,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { styled } from "@mui/material/styles";
import { getCurrencyForInvestmentCompany } from "../utils";

const TableHeaderCell = styled(TableCell)(() => ({
  "&": {
    fontWeight: "600",
  },
}));

type TableComponentProps = {
  locale: Locale;
  tableData: TableData[];
  shortWarning: ShortWarning[];
  isFeeEnabled: boolean;
};

const TableComponent = memo((props: TableComponentProps) => {
  const { locale, tableData, shortWarning } = props;
  const {
    localeString,
    headerFonds,
    headerEndResult,
    headerEndResultInclusive,
    headerPerformance,
    headerOnlineOrder,
    ctaButtonBuy,
  } = localization[locale];

  let endResultHeader = headerEndResult;
  if (props.isFeeEnabled) {
    endResultHeader += " (" + headerEndResultInclusive + ")";
  }
  if(`${[Locale.enRO, Locale.roRO]}`.includes(locale.toString())) {
    endResultHeader += " [1]";
  } else {
    endResultHeader += " [2]";
  }
  if (tableData.length === 0) return null;

  function getUrlByLocale(masterISIN: string) {
    if (`${[Locale.enSK, Locale.skSK]}`.includes(locale.toString())) {
      return `${CTA_SK_ENDPOINT}`;
    } else if(`${[Locale.enRO, Locale.roRO]}`.includes(locale.toString())) {
      return `${CTA_RO_ENDPOINT}`;
    } else if(`${[Locale.enHR, Locale.hrHR]}`.includes(locale.toString())) {
      return `${CTA_HR_ENDPOINT}`;
    }
    return `${CTA_ENDPOINT}/${masterISIN}`
  }

  return (
    <TableContainer component={Box} sx={{ paddingTop: 2, paddingBottom: 2 }}>
      <Table sx={{ minWidth: 860 }} size="small">
        <TableHead>
          <TableRow sx={{ borderColor: COLOR.grey7 }}>
            <TableHeaderCell>{headerFonds}</TableHeaderCell>
            <TableHeaderCell align="right">{endResultHeader}</TableHeaderCell>
            <TableHeaderCell align="right">{headerPerformance}</TableHeaderCell>
            <TableHeaderCell align="right">{headerOnlineOrder}</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData.map(({ masterISIN, label, investmentCompanyShort, finalAmount, totalPerformance }, index) =>
            // Check if finalAmount and totalPerfomance are done calculating and display the data
            finalAmount && totalPerformance ? (
              <TableRow key={label} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                {/* Label */}
                <TableCell component="th" scope="row">
                  {label}
                </TableCell>
                {/* Final Amount */}
                <TableCell align="right">
                  {shortWarning[index]?.isHidden === false ? (
                    // Check if finalAmount needs to be hidden (for funds shorter than main fund duration)
                    <>
                      {new Intl.NumberFormat(localeString, {
                        style: "currency",
                        currency:  getCurrencyForInvestmentCompany({
                          investmentCompanyShort: investmentCompanyShort,
                          locale: localization[locale],
                        }),
                      }).format(finalAmount)}
                    </>
                  ) : (
                    <>-</>
                  )}
                </TableCell>
                {/* Total Performance */}
                <TableCell align="right">
                  {shortWarning[index]?.isHidden === false ? (
                    // Check if totalPerformance needs to be hidden (for funds shorter than main fund duration)
                    <>
                      {new Intl.NumberFormat(localeString, {
                        style: "percent",
                        maximumFractionDigits: 2,
                        signDisplay: "exceptZero",
                      }).format(totalPerformance)}
                    </>
                  ) : (
                    <>-</>
                  )}
                </TableCell>
                {/* CTA Button */}
                <TableCell align="right">
                  <Button
                    onClick={() =>
                      window.open(getUrlByLocale(masterISIN), "_blank", "noopener,noreferrer")
                    }
                    variant="contained"
                    sx={{
                      backgroundColor: COLOR.orange2,
                      boxShadow: 0,
                      border: 1,
                      textTransform: "none",
                      fontWeight: "600",
                      borderColor: COLOR.orange2,
                      "&:hover": {
                        backgroundColor: COLOR.orange3,
                        boxShadow: 2,
                        border: 1,
                        borderColor: COLOR.orange2,
                      },
                    }}
                  >
                    {ctaButtonBuy}
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              // Otherwise display loading skeleton
              <TableRow key={label} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                <TableCell component="th" scope="row">
                  {label}
                </TableCell>
                <TableCell align="right">
                  <Skeleton variant="text" />
                </TableCell>
                <TableCell align="right">
                  <Skeleton variant="text" />
                </TableCell>
                <TableCell align="right">
                  <Button
                    onClick={() =>
                      window.open(`${CTA_ENDPOINT}/${masterISIN}`, "_blank", "noopener,noreferrer")
                    }
                    variant="contained"
                    sx={{
                      backgroundColor: COLOR.orange2,
                      boxShadow: 0,
                      border: 1,
                      textTransform: "none",
                      fontWeight: "600",
                      borderColor: COLOR.orange2,
                      "&:hover": {
                        backgroundColor: COLOR.orange3,
                        boxShadow: 2,
                        border: 1,
                        borderColor: COLOR.orange2,
                      },
                    }}
                  >
                    {ctaButtonBuy}
                  </Button>
                </TableCell>
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
      <Divider />
    </TableContainer>
  );
});

export default TableComponent;
